/**
 * Shrink a user-selected image in the browser before it goes to
 * `POST /api/images`.
 *
 * The server resizes to fit inside `maxDimension` and re-encodes at `quality`
 * anyway, so a 45MB 48MP JPEG dragged off a desktop is mostly wasted upload —
 * and anything past the server's 20MB ceiling is refused outright. Drawing it
 * onto a canvas at the target size first yields the same stored image from a
 * 1–3MB request.
 *
 * Best effort: anything the browser can't decode (or a canvas that can't be
 * had, e.g. in SSR) falls back to the original file, which is exactly what we
 * sent before.
 */
export type PrepareImageOptions = {
  /** Ceiling on the long edge, in pixels. Never upscales. */
  maxDimension: number;
  /** JPEG quality, 1-100. Ignored for PNG (lossless). */
  quality: number;
};

/**
 * A JPEG/PNG under this size that already fits inside the dimension ceiling
 * is sent untouched: re-encoding costs a generation of JPEG loss for no
 * bandwidth win.
 */
export const MAX_PASSTHROUGH_BYTES = 4 * 1024 * 1024;

// Canvas can't round-trip these (animation, vector data); the server handles
// them itself.
const PASSTHROUGH_TYPES = new Set(["image/gif", "image/svg+xml", "image/webp"]);
const HEIC_TYPES = new Set(["image/heic", "image/heif"]);

function isHeicFile(file: File): boolean {
  return HEIC_TYPES.has(file.type) || /\.(heic|heif|hif)$/i.test(file.name);
}

function replaceExtension(name: string, ext: string): string {
  const dot = name.lastIndexOf(".");
  return `${dot > 0 ? name.slice(0, dot) : name || "image"}.${ext}`;
}

function toBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  quality: number,
): Promise<Blob | null> {
  if ("convertToBlob" in canvas) {
    return canvas.convertToBlob({ type, quality }).catch(() => null);
  }
  return new Promise((resolve) =>
    (canvas as HTMLCanvasElement).toBlob(resolve, type, quality),
  );
}

export async function prepareImageForUpload(
  file: File,
  { maxDimension, quality }: PrepareImageOptions,
): Promise<File> {
  if (PASSTHROUGH_TYPES.has(file.type)) return file;
  if (typeof createImageBitmap !== "function") return file;

  const isPng = file.type === "image/png";
  const isHeic = isHeicFile(file);
  let bitmap: ImageBitmap;
  try {
    // Bake EXIF orientation in so the pixels match what the user saw.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file;
  }

  try {
    const longEdge = Math.max(bitmap.width, bitmap.height);
    const needsResize = longEdge > maxDimension;
    // PNGs re-encode to roughly the same size, so bytes alone don't justify
    // a pass over one; dimensions do.
    const tooManyBytes = !isPng && file.size > MAX_PASSTHROUGH_BYTES;
    if (!isHeic && !needsResize && !tooManyBytes) return file;

    const scale = needsResize ? maxDimension / longEdge : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    let canvas: HTMLCanvasElement | OffscreenCanvas;
    if (typeof OffscreenCanvas !== "undefined") {
      canvas = new OffscreenCanvas(width, height);
    } else if (typeof document !== "undefined") {
      canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
    } else {
      return file;
    }
    const ctx = canvas.getContext("2d") as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const type = isPng ? "image/png" : "image/jpeg";
    const blob = await toBlob(canvas, type, quality / 100);
    if (!blob || blob.size === 0) return file;
    return new File([blob], replaceExtension(file.name, isPng ? "png" : "jpg"), {
      type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
